/**
 * useTextSelection — observa la selección de texto activa del documento
 * y la captura cuando el user termina de seleccionar (mouseup).
 * (Doc 21 Fase 5.e — excerpt capture)
 *
 * Diseñado para que el panel de comentarios capture automáticamente
 * el texto que el reviewer seleccionó en el cuerpo del artifact, sin
 * requerir clicks adicionales.
 *
 * Comportamiento:
 *   - Listener global a `mouseup`. Cuando el user termina de
 *     seleccionar, leemos `window.getSelection()`.
 *   - Solo captura si la selección está DENTRO de un elemento con
 *     `data-section-id` (ej. una sección del artifact viewer). Si la
 *     selección es de texto en el panel de comments mismo, ignorada.
 *   - Si captura, expone `snapshot` (texto, max 500 chars) y avisa a
 *     `onCapture` con el data-section-id del ancestro, para que el form
 *     elija esa sección en el mismo evento (sin efectos que copien estado).
 *   - El user puede limpiar manualmente con `clear()`. Una nueva
 *     selección sobreescribe.
 *
 * Patrón de uso:
 *
 *   const { snapshot, clear } = useTextSelection({
 *     onCapture: (sectionId) => setSelectedSection(sectionId),
 *   })
 *
 *   {snapshot ? <blockquote>{snapshot}</blockquote> : null}
 *
 *   await createComment({ ..., excerpt: snapshot })
 *   clear()
 *
 * SSR-safe (no-op si no hay `window`).
 */

import { useCallback, useEffect, useState } from 'react'

const EXCERPT_MAX_LENGTH = 500
const SECTION_DATA_ATTR = 'data-section-id'

export interface UseTextSelectionResult {
  /** Snapshot del último texto capturado. Null si nunca hubo
   *  selección válida o `clear()` lo limpió. */
  snapshot: string | null
  /** Limpia el snapshot manualmente (después de submit/cancel). */
  clear: () => void
  /** Captura imperativa (backup keyboard-only o si el mouseup
   *  listener no se disparó). */
  capture: () => void
}

export interface UseTextSelectionOptions {
  /** Se llama con el section_id del ancestro `[data-section-id]` de cada
   *  selección capturada. Permite auto-poblar el combo del comment form. */
  onCapture?: (sectionId: string) => void
}

export function useTextSelection(
  { onCapture }: UseTextSelectionOptions = {},
): UseTextSelectionResult {
  const [snapshot, setSnapshot] = useState<string | null>(null)

  const captureFromCurrentSelection = useCallback(() => {
    if (typeof window === 'undefined') return
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) return
    const raw = selection.toString().trim()
    if (!raw) return

    // Resolver el elemento DOM donde arranca la selección.
    const anchor = selection.anchorNode
    if (!anchor) return
    const startEl: Element | null =
      anchor.nodeType === Node.ELEMENT_NODE
        ? (anchor as Element)
        : anchor.parentElement
    if (!startEl) return

    // Buscar el ancestro con data-section-id. Si no existe, la
    // selección NO es del cuerpo del artifact (es del propio panel
    // de comments, de un menú, etc.) — ignorar.
    const sectionEl = startEl.closest<HTMLElement>(`[${SECTION_DATA_ATTR}]`)
    if (!sectionEl) return
    const sectionId = sectionEl.getAttribute(SECTION_DATA_ATTR)
    if (!sectionId) return

    // Truncar a límite del backend, agregar elipsis si truncamos.
    const text =
      raw.length > EXCERPT_MAX_LENGTH
        ? raw.slice(0, EXCERPT_MAX_LENGTH - 1) + '…'
        : raw
    setSnapshot(text)
    onCapture?.(sectionId)
  }, [onCapture])

  // Listener pasivo a `mouseup` — la forma natural de detectar
  // "terminé de seleccionar". Filtramos en el handler para evitar
  // capturas falsas (clicks sin selección, selecciones colapsadas).
  useEffect(() => {
    if (typeof window === 'undefined') return
    function handleMouseUp() {
      // Pequeño delay para que la selección se estabilice (en algunos
      // browsers el `mouseup` se dispara antes de que la selección
      // refleje el rango final).
      window.setTimeout(captureFromCurrentSelection, 0)
    }
    document.addEventListener('mouseup', handleMouseUp)
    return () => document.removeEventListener('mouseup', handleMouseUp)
  }, [captureFromCurrentSelection])

  const clear = useCallback(() => {
    setSnapshot(null)
  }, [])

  return {
    snapshot,
    clear,
    capture: captureFromCurrentSelection,
  }
}
